import React from "react";
import * as UI from "./ui.jsx";
// App shell — Inglis blue header. Desktop: full top nav.
// Mobile (≤740px): bottom tabs Home/Stable/Inspections/Live + hamburger sheet for the rest.

const { useState: useStateSh, useEffect: useEffectSh } = React;

const NAV = [
  { id: "home",      label: "Home",        icon: "home",      modules: ["foundation.vendor_registry", "foundation.sale_lot_mirror"] },
  { id: "stats",     label: "Stats",       icon: "eye",       modules: ["vendor.stats"] },
  { id: "stable",    label: "Stable",      icon: "horse",     modules: ["foundation.sale_lot_mirror"] },
  { id: "live",      label: "Live",        icon: "cam",       modules: ["live.parade"] },
  { id: "inspect",   label: "Inspections", icon: "clipboard", modules: ["inspections"] },
  { id: "tv",        label: "TV",          icon: "tv",        modules: ["tv"] },
  { id: "admin",     label: "Admin",       icon: "shield",    modules: ["vendor.admin"] },
];
const MOBILE_TABS = ["home", "stats", "stable", "inspect", "live"];

// Presentation scope: the server decides which sections exist at all
// (VENDORDASH_UI_SECTIONS), and the nav never offers one that is switched off.
const enabled = () => window.VD.sections || [];
const navItems = () => NAV.filter(n => enabled().includes(n.id));
const mobileTabs = () => MOBILE_TABS.filter(id => enabled().includes(id)).slice(0, 5);

function useIsMobile() {
  const [m, setM] = useStateSh(() => window.matchMedia("(max-width: 740px)").matches);
  useEffectSh(() => {
    const mq = window.matchMedia("(max-width: 740px)");
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return m;
}

// A strip across the top whenever any section on screen is fixture-backed.
// Design work needs the fixtures; nobody should ever mistake them for a real draft.
function MockBar() {
  const mocked = window.VD.mockedModules || [];
  if (mocked.length === 0) return null;
  return (
    <div className="mockbar">
      <span>Mock data · {window.VD.environment}</span>
      <span className="mockbar-list">{mocked.map(UI.moduleLabel).join(" · ")}</span>
    </div>
  );
}

function Shell({ view, setView, title, eyebrow, right, children, onBack }) {
  const { Icon, LiveClock } = UI;
  const mobile = useIsMobile();
  const [menu, setMenu] = useStateSh(false);
  const go = (id) => { setMenu(false); setView(id); };
  const Ic = ({ n, s = 18 }) => React.createElement(Icon[n], { style: { width: s, height: s } });
  const vendor = window.DATA.VENDOR;
  const sale = window.DATA.SALE;

  return (
    <div className={`app ${mobile ? "mobile" : ""}`}>
      <header className="hdr">
        <div className="hdr-left">
          {window.VD.logo
            ? <img className="hdr-logo" src={window.VD.logo} alt="Inglis"/>
            : <span className="display" style={{ color: "#fff", fontSize: 22, letterSpacing: ".12em" }}>INGLIS</span>}
          {!mobile && (
            <nav className="hdr-nav">
              {navItems().map(it => (
                <button key={it.id} className={view === it.id ? "active" : ""} onClick={() => go(it.id)}>
                  <Ic n={it.icon}/><span>{it.label}</span>
                </button>
              ))}
            </nav>
          )}
        </div>
        <div className="hdr-right">
          {!mobile && sale.sells && <span className="live-pill"><span className="pulse"/>Sells {sale.daysOut === 1 ? "tomorrow" : sale.sells}</span>}
          <LiveClock/>
          {!mobile && vendor.initials && <div className="hdr-user" title={vendor.staff}>{vendor.initials}</div>}
          {mobile && navItems().some(n => !mobileTabs().includes(n.id)) && (
            <button className="burger" onClick={() => setMenu(true)} aria-label="Menu"><Ic n="burger" s={22}/></button>
          )}
        </div>
      </header>

      <MockBar/>

      <div className="subbar">
        <div className="title-block">
          <div className="crumbs">
            {onBack && (
              <button className="row" onClick={onBack} style={{ color: "var(--inglis-blue)", padding: 0, fontWeight: 700 }}>
                <Icon.back style={{ width: 14, height: 14 }}/> <span>Back</span>
                <span className="dot" style={{ color: "var(--fg-muted)" }}/>
              </button>
            )}
            <span>{eyebrow}</span>
          </div>
          <h1>{title}</h1>
        </div>
        <div className="hdr-right">{right}</div>
      </div>

      <div className="scroll">{children}</div>

      {mobile && mobileTabs().length > 1 && (
        <nav className="tabbar">
          {mobileTabs().map(id => {
            const it = NAV.find(n => n.id === id);
            return (
              <button key={id} className={view === id ? "active" : ""} onClick={() => go(id)}>
                <Ic n={it.icon} s={21}/><span>{it.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {mobile && menu && (
        <div className="sheet-wrap" onClick={() => setMenu(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="row" style={{ justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <div className="eyebrow">{vendor.name}</div>
                <div style={{ fontSize: 13, color: "var(--fg-muted)", marginTop: 3 }}>{vendor.staff} · {vendor.barn}</div>
              </div>
              <button onClick={() => setMenu(false)} style={{ color: "var(--fg-2)" }}><Ic n="close" s={22}/></button>
            </div>
            {navItems().filter(n => !mobileTabs().includes(n.id)).map(it => (
              <button key={it.id} className={`sheet-item ${view === it.id ? "active" : ""}`} onClick={() => go(it.id)}>
                <Ic n={it.icon} s={20}/><span>{it.label}</span><Icon.arrow style={{ width: 15, height: 15, marginLeft: "auto", opacity: .4 }}/>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export { Shell, NAV, useIsMobile };
